import { getSlideIndices } from "library";
import { clearActionTimeout, initActionStack, playActions } from "./actions";

const currentFragmentIndex = -1;
const noNarrationTime = 4;

let currentBar = null;
let timeout = null;
let elapsedTime;
let playing = false;

let conversationIndex = 0;
let conversationLength = 0;
let conversationPlayed = 0;
let conversation: string[] = [];

function telemetry(type: string) {
  window.parent?.__telemetry?.(type);
}

function debounceShowNote() {
  const slideContainer: any =
    window.parent?.document.getElementById("current-slide")?.childNodes[0];
  if (slideContainer == null) {
    return;
  }

  // receieve indices from the next slide
  const { f: fragment, h: slide } =
    slideContainer.contentWindow.Reveal.getIndices();

  const document = window.parent.document;
  if (document == null) {
    return;
  }

  // highlight the current speaker note
  for (const note of document.querySelectorAll(
    ".speakerNotes"
  ) as unknown as HTMLDivElement[]) {
    note.style.backgroundColor = "";
    note.style.border = "";
    note.style.borderBottom = "solid 1px #ddd";
  }

  const currentNote = document.getElementById(
    `speaker-notes-${slide}-${fragment}`
  );

  if (currentNote) {
    console.log("Showing note", `speaker-notes-${slide}-${fragment}`);
    currentNote.style.backgroundColor = "lightyellow";
    currentNote.style.border = "1px solid #aaa";

    currentNote.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func.apply(context, args);
    }, wait);
  };
}

const showNote = debounce(debounceShowNote, 100);

export function initialisePlayer() {
  const fragments = getFragments();
  const group = getGroup();
  const hasTracks = fragments?.length > 0;

  if (!hasTracks) {
    if (revealElements.playButton) {
      revealElements.playButton.style.display = "none";
      revealElements.pauseButton.style.display = "none";
      revealElements.playBar.style.display = "none";
    }
  } else {
    Reveal.configure({ progress: false, controls: false });

    const audioPlayer = revealElements.player;

    // add the first track to player to play
    conversationIndex = 0;
    conversation = fragments[0]?.[0]?.tracks[group]?.urls;
    conversationLength = fragments[0]?.[0]?.tracks[group]?.time;
    conversationPlayed = 0;

    audioPlayer.src = conversation?.[0];

    currentBar = document.getElementById(`fragment_0_-1`);

    audioPlayer.addEventListener("timeupdate", () => {
      const percentage =
        ((conversationPlayed + audioPlayer.currentTime) / conversationLength) *
        100;

      if (currentBar) {
        currentBar.style.width = `${percentage}%`;
      }

      // show elapsed time
      revealElements.currentTime.textContent = formatTime(
        elapsedTime + conversationPlayed + audioPlayer.currentTime
      );

      // currentTimeDisplay.textContent = formatTime(audioPlayer.currentTime);
      // if (audioPlayer.duration) {
      //     durationDisplay.textContent = formatTime(audioPlayer.duration);
      // }
    });

    // revealElements.player.addEventListener("play", () =>
    //   telemetry("slide-play")
    // );
    // revealElements.player.addEventListener("pause", () =>
    //   telemetry("slide-pause")
    // );
    // revealElements.player.addEventListener("stop", () =>
    //   telemetry("slide-stop)")
    // );

    audioPlayer.addEventListener("ended", async function () {
      // telemetry("slide-finished");

      conversationIndex++;
      conversationPlayed += audioPlayer.duration;

      if (conversationIndex < conversation.length) {
        revealElements.player.src = conversation[conversationIndex];

        const delay = parseInt(
          conversation[conversationIndex].match(/&d=(\d+)/)?.[1] || "0"
        );

        let currentDelay = 0;
        while (currentDelay < delay) {
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve(true);
            }, 100);

            conversationPlayed += 0.1;
            const percentage =
              ((conversationPlayed + audioPlayer.currentTime) /
                conversationLength) *
              100;

            if (currentBar) {
              currentBar.style.width = `${percentage}%`;
            }

            // show elapsed time
            revealElements.currentTime.textContent = formatTime(
              elapsedTime + conversationPlayed + audioPlayer.currentTime
            );
          });
          currentDelay += 100;
        }

        revealElements.player.play();
      } else {
        conversationIndex = 0;
        if (window.__revealFragment?.classList.contains("pause-after")) {
          pauseTrack();
          window.__revealPaused = true;
        } else {
          nextSlide();
        }
      }
    });

    // Event listener for slide changes
    Reveal.addEventListener("slidechanged", function (event) {
      window.parent?.__telemetry?.("slide-change", {
        index: Reveal.getIndices().h,
      });

      clearNoNarrative();
      if (playing) {
        playTrack();
      } else {
        markTrack();
      }
      showNote();
    });

    // Event listener for showing fragments
    initTotalTime();

    // generate html of the player
    generatePlayer();
  }

  Reveal.addEventListener("fragmentshown", async function (event) {
    //@ts-ignore
    window.__revealFragment = event.fragment;

    if (hasTracks) {
      clearNoNarrative();

      if (playing) {
        if (event.fragment.classList.contains("pause-before")) {
          pauseTrack();
        } else {
          playTrack();
        }
      } else {
        markTrack();
      }
    }
    showNote();
  });

  // Event listener for hiding fragments
  Reveal.addEventListener("fragmenthidden", async function (event) {
    if (hasTracks) {
      clearNoNarrative();

      if (playing) {
        playTrack();
      } else {
        markTrack();
      }
    }
    showNote();
  });
}

export function setGroup(group: number) {
  window.group = group;

  revealElements.player.pause();

  // current time
  const currentTime = revealElements.player.currentTime;

  // new tracks probably have different time
  initTotalTime();

  // set current track
  const fragments = getFragments();

  const slide = Reveal.getIndices().h;
  const { fragment } = getSlideIndices();

  conversation = fragments[slide]?.[fragment + 1]?.tracks[group]?.urls;
  conversationLength = fragments[slide]?.[fragment + 1]?.tracks[group]?.time;
  conversationPlayed = 0;
  const track = conversation?.[0];

  if (track != null && track != "") {
    if (track.startsWith("http")) {
      revealElements.player.src = track;
      revealElements.player.currentTime = 0;
    } else if (!isNaN(parseFloat(track))) {
      noNarrative(parseFloat(track));
    }
  } else {
    // missing narrative for this slide
    noNarrative();
  }

  revealElements.player.play();
}

const preloadAudio = (url) => {
  fetch(url, {
    method: "GET",
    headers: new Headers(), // empty headers
    mode: "cors",
  })
    .then((res) => console.log(`Preloaded: ${url}`))
    .catch((err) => console.error(`Failed to preload: ${url}`, err));
};

export function playTrack() {
  if (window.__revealPaused == true) {
    window.__revealPaused = null;
    nextSlide();
    playTrack();
    return;
  }

  const slide = Reveal.getIndices().h;
  const { fragment } = getSlideIndices();

  markTrack();

  const fragments = getFragments();
  const group = getGroup();

  const player = getPlayer();
  conversation = fragments[slide]?.[fragment + 1]?.tracks[group]?.urls;
  conversationLength = fragments[slide]?.[fragment + 1]?.tracks[group]?.time;
  conversationPlayed = 0;

  // preload upcoming conversation
  if (conversation?.length > 0) {
    for (const conv of conversation.slice(1)) {
      // Call with dynamic file URL
      preloadAudio(conv);
    }
  }

  // preload next slide
  let nextNarration =
    fragments[slide]?.[fragment + 2]?.tracks[group]?.urls?.[0];
  if (nextNarration == null) {
    nextNarration = fragments[slide + 1]?.[0]?.tracks[group]?.urls?.[0];
  }
  if (nextNarration != null) {
    preloadAudio(nextNarration);
  }

  const track = conversation?.[0];
  if (track != null && track != "") {
    if (track.startsWith("http")) {
      player.src = track;
      play();
    } else if (!isNaN(parseFloat(track))) {
      noNarrative(parseFloat(track));
    }
  } else {
    // missing narrative for this slide
    noNarrative();
  }

  // play actions
  clearActionTimeout();

  const actions = fragments[slide]?.[fragment + 1]?.actions;
  if (actions) {
    initActionStack(actions);
    playActions();
  }

  // play video
  const section = Reveal.getCurrentSlide();
  const video = section.querySelector("video");
  if (video) {
    video.play();
  }
}

export function pauseTrack() {
  clearNoNarrative();
  pause();

  // pause video
  const section = Reveal.getCurrentSlide();
  const video = section.querySelector("video");
  if (video) {
    video.pause();
  }
}

export function nextSlide() {
  conversationIndex = 0;

  const slide = Reveal.getIndices().h;
  const { fragment } = getSlideIndices();

  revealElements.player.pause();
  clearNoNarrative();
  Reveal.right();

  // check if slide and fragment remained the same, we'll pause
  // this means that we are probably at
  if (
    slide == Reveal.getIndices().h &&
    fragment == getSlideIndices().fragment
  ) {
    telemetry("slides-finished");
    pause();
  }
}

export function previousSlide() {
  revealElements.player.pause();
  clearNoNarrative();
  Reveal.left();
}

export function seekTrack(e, el) {
  const audioPlayer = revealElements.player;
  const percent = e.offsetX / e.currentTarget.offsetWidth;
  audioPlayer.currentTime = percent * audioPlayer.duration;

  // seek video
  const section = Reveal.getCurrentSlide();
  const video = section.querySelector("video");
  if (video && video.duration >= audioPlayer.currentTime) {
    video.currentTime = audioPlayer.currentTime;
    video.play();
  }
}

export function navigateToSlideAndFragment(
  horizontalIndex,
  verticalIndex,
  fragmentIndex,
  event,
  element
) {
  const { slide, fragment } = getSlideIndices();
  if (horizontalIndex == slide && fragmentIndex == fragment) {
    seekTrack(event, element);
  } else {
    revealElements.player.pause();
    Reveal.slide(horizontalIndex, 0, fragmentIndex);
  }
}

export function countFragments() {
  const sections = document.querySelectorAll("section");
  return Array.from(sections).map((section) => {
    let count = 1;
    const indices = [];
    const fragments = section.querySelectorAll(".fragment");

    for (const fragment of Array.from(fragments)) {
      const index = fragment.getAttribute("data-fragment-index");
      if (index == null) {
        count++;
      } else if (indices.indexOf(index) === -1) {
        if (index != "-1") {
          count++;
        }
        indices.push(index);
      }
    }
    return count;
  });
}

// generate player

export function generatePlayer() {
  const sectionFragments = countFragments();
  const playBar = document.getElementById("playBar");

  if (playBar == null) {
    return;
  }

  const fragments = getFragments();
  const group = getGroup();

  playBar.innerHTML = sectionFragments
    .map((fragmentCount, slideIndex) => {
      return `
  
    ${Array.from({ length: fragmentCount })
      .map((_, fragmentIndex) => {
        const narration = fragments[slideIndex]?.[fragmentIndex];
        return `
    <div title="${narration == null || !narration?.tracks[group]?.urls[0] ? "Narration is missing for this slide" : ""}" 
         class="spBarTickContainer ${narration == null || !narration?.tracks[group]?.urls[0] ? "narrationMissing" : ""}" 
         onclick="SkillPiesPresentation.navigateToSlideAndFragment(${slideIndex},0,${fragmentIndex - 1}, event, this)">
      <img class="spBarTn" src="${narration?.tn || "/images/wireframe1610.png"}" onerror="this.onerror=null; this.src='/images/wireframe.png';" />
      <div class="spBarTick" onclick="SkillPiesPresentation.clickSeekTrack(event, this);" id="fragment_${slideIndex}_${fragmentIndex - 1}"></div>
    </div>`;
      })
      .join("")} 
`;
    })
    .join("");
}

// PRIVATE

const revealElements = {
  /**
   * @returns {HTMLButtonElement} The audio player element.
   */
  get playButton() {
    return document.getElementById("playVideo");
  },
  /**
   * @returns {HTMLDivElement} The audio player element.
   */
  get playerControls() {
    return document.getElementById("playerControls");
  },
  /**
   * @returns {HTMLButtonElement} The audio player element.
   */
  get pauseButton() {
    return document.getElementById("pauseVideo");
  },
  /**
   * @returns {HTMLAudioElement} The audio player element.
   */
  get player(): HTMLAudioElement {
    return document.getElementById("audioPlayback") as HTMLAudioElement;
  },
  /**
   * @returns {HTMLDivElement} The audio player element.
   */
  get playBar() {
    return document.getElementById("playBar");
  },
  /**
   * @returns {HTMLDivElement} The audio player element.
   */
  get missingNarrative() {
    return document.getElementById("missingNarrative");
  },
  /**
   * @returns {HTMLDivElement} The audio player element.
   */
  get currentTime() {
    return document.getElementById("currentTime");
  },
  /**
   * @returns {HTMLDivElement} The audio player element.
   */
  get totalTime() {
    return document.getElementById("totalTime");
  },
  /**
   * @returns {HTMLDivElement} The audio player element.
   */
  slide(slide, fragment) {
    return document.getElementById(`fragment_${slide}_${fragment}`);
  },
};

let player = null;

function initTotalTime() {
  const fragments = getFragments();
  const group = getGroup();

  // init total time
  const allFragments = countFragments();
  const totalTime = allFragments.reduce((acc, fragmentCount, slideIndex) => {
    const count = Array.from({ length: fragmentCount }).reduce(
      (time: number, _, fragmentIndex) => {
        return (
          time +
          (fragments[slideIndex]?.[fragmentIndex]?.tracks[group]?.time ||
            noNarrationTime)
        );
      },
      0
    ) as number;
    return acc + count;
  }, 0);
  revealElements.totalTime.textContent = formatTime(totalTime);
}

function formatTime(time) {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

function noNarrative(seconds = noNarrationTime) {
  revealElements.playButton.style.display = "none";
  revealElements.pauseButton.style.display = "block";

  if (seconds <= 0) {
    revealElements.missingNarrative.style.display = "none";
    nextSlide();
    return;
  }
  revealElements.missingNarrative.style.display = "block";
  revealElements.missingNarrative.innerHTML = `This slide has no narrative (next slide in ${Math.ceil(seconds)} second${Math.ceil(seconds) > 1 ? "s" : ""})`;
  timeout = setTimeout(() => {
    noNarrative(seconds - 0.25);
  }, 250);
}

function play() {
  playing = true;
  revealElements.player.play();
  revealElements.playButton.style.display = "none";
  revealElements.pauseButton.style.display = "block";
  revealElements.playerControls.classList.add("playingControls");
}

function pause() {
  playing = false;
  revealElements.player.pause();
  revealElements.playButton.style.display = "block";
  revealElements.pauseButton.style.display = "none";

  revealElements.playerControls.classList.remove("playingControls");
}

function clearNoNarrative() {
  clearTimeout(timeout);
  // timeout = null;
  revealElements.missingNarrative.style.display = "none";
}

function getPlayer() {
  if (player == null) {
    player = revealElements.player;
  }
  return player;
}

function markTrack() {
  const slide = Reveal.getIndices().h;
  const { fragment } = getSlideIndices();

  //@ts-ignore
  window.parent.window.__revealFragment = { slide, fragment };

  currentBar = document.getElementById(`fragment_${slide}_${fragment}`);
  elapsedTime = 0;

  const slides = countFragments();
  const fragments = getFragments();
  const group = getGroup();

  // hide all bars
  for (let i = 0; i < slides.length; i++) {
    const slideFragments = Array.from({ length: slides[i] });
    for (let j = -1; j < slideFragments.length - 1; j++) {
      if (i < slide || (i == slide && j < fragment)) {
        revealElements.slide(i, j).style.width = "100%";
        elapsedTime +=
          fragments[i]?.[j + 1]?.tracks[group]?.time || noNarrationTime;
      } else if (i > slide || (i == slide && j > fragment)) {
        const slide = revealElements.slide(i, j);
        if (slide) {
          slide.style.width = "0%";
        } else {
          console.error(
            "Please refresh your piePlayer as there are less fragments than expected."
          );
        }
      }
    }
  }

  revealElements.currentTime.textContent = formatTime(elapsedTime);
}

export function getGroup() {
  return window.group;
}

export function getFragments() {
  return window.fragments;
}

export function clickPlay() {
  telemetry("slide-click-play");
  playTrack();
}

export function clickPause() {
  telemetry("slide-click-pause");
  pauseTrack();
}

export function clickNextSlide() {
  telemetry("slide-click-next");
  nextSlide();
}

export function clickPreviousSlide() {
  telemetry("slide-click-previous");
  previousSlide();
}

export function clickSeekTrack(e, el) {
  window.parent?.__telemetry?.("slide-click-seek");
  seekTrack(e, el);
}
